import { t } from "@lingui/macro"
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material"
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { FastApiPropertyLoaderModalStore } from "./store"

import { AdminService } from "src/api"
import { avy_api_pkg_common_IntegrationWithType } from "src/api/models/avy_api_pkg_common_IntegrationWithType"
import { ModalHeader } from "src/components/ModalHeader"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { IProperty } from "src/views/client-management/property/store"
import { IPropertyOwner } from "src/views/client-management/store"
import { ConfirmModal } from "src/modals/confirm"

interface IFastApiPropertyLoaderModalProps {
    propertyOwner: IPropertyOwner
    existingProperties: IProperty[]
    legalEntityId?: number
    legalEntityName?: string
    onPropertiesUpdated?: () => void
    preFilteredConnectors?: avy_api_pkg_common_IntegrationWithType[]
}

interface IFastApiPropertyWithStatus {
    id?: string
    name?: string
    category?: "existing" | "property-owner" | "new"
    isDisabled: boolean
    isSelectable: boolean
    legalEntityId?: number
    [key: string]: unknown
}

const View = observer((props: IFastApiPropertyLoaderModalProps) => {
    const store = useStore(FastApiPropertyLoaderModalStore)
    const globalStore = useStore(GlobalStore)
    const [searchTerm, setSearchTerm] = useState("")
    const [fastApiSearchTerm, setFastApiSearchTerm] = useState("")
    const [selectedIntegrationId, setSelectedIntegrationId] = useState<
        number | ""
    >("")
    const [step, setStep] = useState<"picker" | "data">("picker")
    const [existingProperties, setExistingProperties] = useState<IProperty[]>(
        props.existingProperties,
    )
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(
        new Set(),
    )
    const [allPropertyOwnerProperties, setAllPropertyOwnerProperties] =
        useState<IProperty[]>([])
    const [creationProgress, setCreationProgress] = useState<{
        current: number
        total: number
    }>({ current: 0, total: 0 })

    // Category visibility toggles
    const [showGray, setShowGray] = useState(true)
    const [showOrange, setShowOrange] = useState(true)
    const [showGreen, setShowGreen] = useState(true)

    // Loading states for better UX
    const isLoadingIntegrations = globalStore.loading.is(
        FastApiPropertyLoaderModalStore.LoadingKeys.loadIntegrations,
    )
    const isLoadingProperties = globalStore.loading.is(
        FastApiPropertyLoaderModalStore.LoadingKeys.loadFastApiProperties,
    )
    const isCreatingProperties = globalStore.loading.is(
        FastApiPropertyLoaderModalStore.LoadingKeys.createProperty,
    )

    // Find the selected connector info
    const selectedConnector = useMemo(() => {
        return store.fastApiIntegrations?.find(
            (integration) =>
                integration.integration_id === selectedIntegrationId,
        )
    }, [store.fastApiIntegrations, selectedIntegrationId])

    // Helper function to get legal entity name from session data
    const getLegalEntityName = useCallback(
        (legalEntityId: number): string | null => {
            // Find the current property owner in the session
            const sessionPropertyOwner =
                globalStore.session.propertyOwners.find(
                    (po) =>
                        po.propertyOwnerId ===
                        props.propertyOwner.propertyOwnerId,
                )

            // Find the legal entity in the property owner's legal entities
            const legalEntity = sessionPropertyOwner?.legalEntities?.find(
                (le) => le.legalEntityId === legalEntityId,
            )

            return legalEntity?.legalName ?? null
        },
        [
            globalStore.session.propertyOwners,
            props.propertyOwner.propertyOwnerId,
        ],
    )

    // Load all properties under the property owner
    useEffect(() => {
        const loadAllPropertyOwnerProperties = async () => {
            if (props.propertyOwner?.propertyOwnerId == null) return

            try {
                const allProperties =
                    await AdminService.postV1AdminPropertySearch({
                        request: {
                            property_owner_ids: [
                                props.propertyOwner.propertyOwnerId,
                            ],
                        },
                    })

                const mappedProperties: IProperty[] = allProperties.map(
                    (property) => ({
                        city: property.city ?? "",
                        createdDate: property.created_date ?? "",
                        externalId: property.external_id ?? "",
                        integrationId: property.integration_id ?? undefined,
                        integrationActive: property.integration_active ?? false,
                        integrationType: property.integration_type ?? "",
                        leaflet: property.leaflet ?? "",
                        legalEntityId: property.legal_entity_id ?? 0,
                        modifiedDate: property.modified_date ?? "",
                        name: property.name ?? "",
                        propertyId: property.property_id ?? 0,
                        propertyOwnerId: property.property_owner_id ?? 0,
                        selfOnboardingCode: property.self_onboarding_code ?? "",
                        selfOnboardingEnabled:
                            property.self_onboarding_enabled ?? false,
                        id: property.property_id ?? 0,
                    }),
                )

                setAllPropertyOwnerProperties(mappedProperties)
            } catch (error) {
                setAllPropertyOwnerProperties([])
            }
        }

        void loadAllPropertyOwnerProperties()
    }, [props.propertyOwner?.propertyOwnerId])

    useEffect(() => {
        void store.init(
            props.propertyOwner,
            globalStore.session.accessGroupId,
            props.legalEntityId,
            props.onPropertiesUpdated,
            props.preFilteredConnectors,
        )
    }, [
        store,
        props.propertyOwner,
        globalStore.session.accessGroupId,
        props.legalEntityId,
        props.onPropertiesUpdated,
        props.preFilteredConnectors,
    ])

    // Update existing properties when props change
    useEffect(() => {
        setExistingProperties(props.existingProperties)
    }, [props.existingProperties])

    // Auto-select integration if property owner has one associated
    useEffect(() => {
        if (
            store.selectedIntegrationId !== null &&
            store.selectedIntegrationId > 0
        ) {
            setSelectedIntegrationId(store.selectedIntegrationId)
        }
    }, [store.selectedIntegrationId])

    const handleIntegrationSelect = useCallback(async () => {
        if (Boolean(selectedIntegrationId) && selectedIntegrationId !== 0) {
            store.setSelectedIntegrationId(selectedIntegrationId as number)
            await store.loadFastApiProperties()
            setStep("data")
        }
    }, [selectedIntegrationId, store])

    const handleClose = useCallback(() => {
        globalStore.modals.pop()
    }, [globalStore])

    const handleBack = useCallback(() => {
        setStep("picker")
    }, [])

    const handleToggleProperty = useCallback((propertyId: string) => {
        setSelectedPropertyIds((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(propertyId)) {
                newSet.delete(propertyId)
            } else {
                newSet.add(propertyId)
            }
            return newSet
        })
    }, [])

    const filteredExistingProperties = useMemo(() => {
        if (searchTerm.length === 0) return existingProperties
        const lowerSearchTerm = searchTerm.toLowerCase()
        return existingProperties.filter(
            (property) =>
                property.name.toLowerCase().includes(lowerSearchTerm) ||
                (property.externalId ?? "")
                    .toLowerCase()
                    .includes(lowerSearchTerm),
        )
    }, [existingProperties, searchTerm])

    const fastApiPropertiesWithDisabledStatus = useMemo(() => {
        // Safety check to prevent errors if store properties aren't available
        if (
            store.fastApiProperties == null ||
            !Array.isArray(store.fastApiProperties)
        ) {
            return []
        }

        return store.fastApiProperties.map((fastApiProperty) => {
            // Check if property exists in current left list (existing properties)
            const existsInCurrentList = existingProperties.some(
                (existingProperty) =>
                    existingProperty.externalId === fastApiProperty.id,
            )

            // Check if property exists in all property owner properties
            const matchingOwnerProperty = allPropertyOwnerProperties.find(
                (ownerProperty) =>
                    ownerProperty.externalId === fastApiProperty.id,
            )

            let category: "existing" | "property-owner" | "new" = "new"
            if (existsInCurrentList) {
                category = "existing"
            } else if (matchingOwnerProperty != null) {
                category = "property-owner"
            }

            return {
                ...fastApiProperty,
                isDisabled: existsInCurrentList,
                category,
                isSelectable: category === "new", // Only new properties are selectable
                legalEntityId: matchingOwnerProperty?.legalEntityId, // Add legal entity ID for orange properties
            } as IFastApiPropertyWithStatus
        })
    }, [
        store.fastApiProperties,
        existingProperties,
        allPropertyOwnerProperties,
    ])

    // Calculate category counts
    const categoryCounts = useMemo(() => {
        const counts = { existing: 0, propertyOwner: 0, new: 0 }

        fastApiPropertiesWithDisabledStatus.forEach((property) => {
            const category = property.category ?? "new"
            if (category === "existing") counts.existing++
            else if (category === "property-owner") counts.propertyOwner++
            else if (category === "new") counts.new++
        })

        return counts
    }, [fastApiPropertiesWithDisabledStatus])

    const filteredFastApiProperties = useMemo(() => {
        let filtered = fastApiPropertiesWithDisabledStatus

        // Apply category visibility filters
        filtered = filtered.filter((property) => {
            const category = property.category ?? "new"
            if (category === "existing" && !showGray) return false
            if (category === "property-owner" && !showOrange) return false
            if (category === "new" && !showGreen) return false
            return true
        })

        // Apply search filter
        if (fastApiSearchTerm.length === 0) return filtered
        const lowerSearchTerm = fastApiSearchTerm.toLowerCase()
        return filtered.filter(
            (property: IFastApiPropertyWithStatus) =>
                (property.name ?? "").toLowerCase().includes(lowerSearchTerm) ||
                (property.id ?? "").toLowerCase().includes(lowerSearchTerm),
        )
    }, [
        fastApiPropertiesWithDisabledStatus,
        fastApiSearchTerm,
        showGray,
        showOrange,
        showGreen,
    ])

    const handleSelectAll = useCallback(() => {
        const newPropertyIds = filteredFastApiProperties
            .filter((property) => property.category === "new")
            .map((property) => property.id)
            .filter((id): id is string => id != null)
        setSelectedPropertyIds(new Set(newPropertyIds))
    }, [filteredFastApiProperties])

    const handleClearSelection = useCallback(() => {
        setSelectedPropertyIds(new Set())
    }, [])

    const handleSubmitSelected = useCallback(() => {
        const selectedProperties = filteredFastApiProperties.filter(
            (property) =>
                selectedPropertyIds.has(property.id ?? "") &&
                property.category === "new",
        )

        if (selectedProperties.length === 0) return

        // Show confirmation modal
        globalStore.modals.open(
            () => (
                <ConfirmModal
                    onConfirm={async (confirmed) => {
                        if (confirmed) {
                            // Reset progress
                            setCreationProgress({
                                current: 0,
                                total: selectedProperties.length,
                            })

                            // Create properties in bulk with progress tracking
                            const createdProperties =
                                await store.createPropertiesFromFastApi(
                                    selectedProperties,
                                    props.propertyOwner.propertyOwnerId,
                                    (current, total) => {
                                        setCreationProgress({ current, total })
                                    },
                                )

                            // Add the created properties to local existing properties list
                            if (createdProperties.length > 0) {
                                setExistingProperties((prev) => [
                                    ...prev,
                                    ...createdProperties.map(
                                        (createdProperty) => ({
                                            propertyId:
                                                createdProperty.propertyId,
                                            name: createdProperty.name,
                                            city: createdProperty.city,
                                            externalId:
                                                createdProperty.externalId,
                                            integrationId: undefined,
                                            integrationActive: false,
                                            selfOnboardingEnabled: false,
                                            selfOnboardingCode: "",
                                            leaflet: "",
                                            // Add required fields that we don't have from creation
                                            createdDate:
                                                new Date().toISOString(),
                                            integrationType: "",
                                            legalEntityId:
                                                props.legalEntityId ?? 0,
                                            modifiedDate:
                                                new Date().toISOString(),
                                            propertyOwnerId:
                                                props.propertyOwner
                                                    .propertyOwnerId,
                                            id: createdProperty.propertyId,
                                        }),
                                    ),
                                ])
                            }

                            // Clear selection after successful creation
                            setSelectedPropertyIds(new Set())
                            setCreationProgress({ current: 0, total: 0 })
                        }
                    }}
                    title={t`fastapi-property-loader.confirm-create-title`}
                    content={`${t`fastapi-property-loader.confirm-create-content`} ${
                        selectedProperties.length
                    } ${t`fastapi-property-loader.properties-text`}?`}
                />
            ),
            {
                variant: "slide-up-w600",
            },
        )
    }, [
        filteredFastApiProperties,
        selectedPropertyIds,
        globalStore.modals,
        store,
        props.propertyOwner.propertyOwnerId,
        props.legalEntityId,
    ])

    // Add safety checks for store properties
    const safeIntegrations = store.fastApiIntegrations ?? []
    const safeProperties = store.fastApiProperties ?? []

    if (step === "picker") {
        return (
            <>
                <ModalHeader>
                    <IconButton onClick={handleClose} sx={{ ml: "auto" }}>
                        <CloseIcon />
                    </IconButton>
                </ModalHeader>
                <Box sx={{ p: 3, minWidth: 400 }}>
                    <Typography variant="h6" gutterBottom>
                        {t`fastapi-property-loader.select-integration`}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        {t`fastapi-property-loader.select-integration-description`}
                    </Typography>

                    <FormControl
                        fullWidth
                        sx={{ mb: 3 }}
                        disabled={isLoadingIntegrations || isLoadingProperties}
                    >
                        <InputLabel>{t`fastapi-property-loader.integration-label`}</InputLabel>
                        <Select
                            value={selectedIntegrationId}
                            onChange={(e) =>
                                setSelectedIntegrationId(
                                    e.target.value as number,
                                )
                            }
                            label={t`fastapi-property-loader.integration-label`}
                            disabled={
                                isLoadingIntegrations || isLoadingProperties
                            }
                        >
                            {safeIntegrations.map((integration) => (
                                <MenuItem
                                    key={integration.integration_id}
                                    value={integration.integration_id}
                                >
                                    {integration.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                        }}
                    >
                        <Button
                            onClick={handleClose}
                            color="secondary"
                            disabled={isLoadingProperties}
                        >
                            {t`common.cancel`}
                        </Button>
                        <Button
                            onClick={handleIntegrationSelect}
                            variant="contained"
                            disabled={
                                selectedIntegrationId === "" ||
                                selectedIntegrationId === 0 ||
                                isLoadingIntegrations ||
                                isLoadingProperties
                            }
                            startIcon={
                                isLoadingProperties ? (
                                    <CircularProgress size={16} />
                                ) : undefined
                            }
                        >
                            {isLoadingProperties
                                ? t`common.loading`
                                : t`common.continue`}
                        </Button>
                    </Box>
                </Box>
            </>
        )
    }

    return (
        <>
            <ModalHeader>
                <Button
                    onClick={handleBack}
                    color="secondary"
                    disabled={isCreatingProperties}
                >
                    {t`common.back`}
                </Button>
                <IconButton onClick={handleClose} sx={{ ml: "auto" }}>
                    <CloseIcon />
                </IconButton>
            </ModalHeader>
            <Box sx={{ p: 2, overflow: "hidden" }}>
                {/* Compact Context Header */}
                <Box sx={{ mb: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            p: 1.5,
                            backgroundColor: "grey.50",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                            flexWrap: "wrap",
                        }}
                    >
                        {/* Left side: Property Owner → Legal Entity hierarchy */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {/* Property Owner */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                    }}
                                >
                                    {props.propertyOwner.legalName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        backgroundColor: "grey.200",
                                        px: 0.5,
                                        py: 0.125,
                                        borderRadius: 0.25,
                                        fontFamily: "monospace",
                                        fontSize: "0.65rem",
                                    }}
                                >
                                    {props.propertyOwner.propertyOwnerId}
                                </Typography>
                            </Box>

                            {/* Legal Entity */}
                            {props.legalEntityName != null &&
                                props.legalEntityName.length > 0 && (
                                    <>
                                        {/* Separator */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "text.secondary",
                                                mx: 0.5,
                                            }}
                                        >
                                            →
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "text.primary",
                                                }}
                                            >
                                                {props.legalEntityName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "text.secondary",
                                                    backgroundColor: "grey.200",
                                                    px: 0.5,
                                                    py: 0.125,
                                                    borderRadius: 0.25,
                                                    fontFamily: "monospace",
                                                    fontSize: "0.65rem",
                                                }}
                                            >
                                                {props.legalEntityId}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                        </Box>

                        {/* Right side: Connector */}
                        {selectedConnector != null && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                    }}
                                >
                                    {selectedConnector.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        backgroundColor: "grey.200",
                                        px: 0.5,
                                        py: 0.125,
                                        borderRadius: 0.25,
                                        fontFamily: "monospace",
                                        fontSize: "0.65rem",
                                    }}
                                >
                                    {selectedConnector.integration_id}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {/* Left column - Existing Properties */}
                    <Grid item xs={6}>
                        <Paper sx={{ p: 1.5, height: 500 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={`Search existing properties (${filteredExistingProperties.length})`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                disabled={isCreatingProperties}
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon
                                            sx={{
                                                mr: 1,
                                                color: "text.secondary",
                                            }}
                                        />
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                                <List dense>
                                    {filteredExistingProperties.map(
                                        (property) => (
                                            <React.Fragment
                                                key={property.propertyId}
                                            >
                                                <ListItem
                                                    sx={{
                                                        py: 0.25,
                                                        px: 1,
                                                        mb: 0.25,
                                                        borderRadius: 0.5,
                                                        backgroundColor:
                                                            "grey.50",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "grey.100",
                                                        },
                                                        minHeight: "auto",
                                                    }}
                                                >
                                                    <ListItemText
                                                        sx={{ m: 0 }}
                                                        primary={
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    justifyContent:
                                                                        "space-between",
                                                                    alignItems:
                                                                        "center",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        color: "text.primary",
                                                                        fontSize:
                                                                            "0.75rem",
                                                                    }}
                                                                >
                                                                    {
                                                                        property.name
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: "text.secondary",
                                                                        backgroundColor:
                                                                            "grey.200",
                                                                        px: 0.5,
                                                                        py: 0.125,
                                                                        borderRadius: 0.25,
                                                                        fontFamily:
                                                                            "monospace",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                    }}
                                                                >
                                                                    {Boolean(
                                                                        property.externalId,
                                                                    )
                                                                        ? property.externalId
                                                                        : "—"}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            </React.Fragment>
                                        ),
                                    )}
                                    {filteredExistingProperties.length ===
                                        0 && (
                                        <ListItem>
                                            <ListItemText
                                                primary={t`fastapi-property-loader.no-properties-found`}
                                                sx={{
                                                    textAlign: "center",
                                                    color: "text.secondary",
                                                }}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right column - FastAPI Properties */}
                    <Grid item xs={6}>
                        <Paper sx={{ p: 1.5, height: 500 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={`Search FastAPI properties (${filteredFastApiProperties.length})`}
                                value={fastApiSearchTerm}
                                onChange={(e) =>
                                    setFastApiSearchTerm(e.target.value)
                                }
                                disabled={isCreatingProperties}
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon
                                            sx={{
                                                mr: 1,
                                                color: "text.secondary",
                                            }}
                                        />
                                    ),
                                }}
                                sx={{ mb: 1 }}
                            />

                            {/* Modern Filter & Action Toolbar */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 2,
                                    flexWrap: "wrap",
                                }}
                            >
                                {/* Left: Category Filter Chips */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0.5,
                                        alignItems: "center",
                                    }}
                                >
                                    <Chip
                                        label={`Existing (${categoryCounts.existing})`}
                                        size="small"
                                        variant={
                                            showGray ? "filled" : "outlined"
                                        }
                                        onClick={() => setShowGray(!showGray)}
                                        disabled={isCreatingProperties}
                                        sx={{
                                            backgroundColor: showGray
                                                ? "grey.600"
                                                : "transparent",
                                            color: showGray
                                                ? "white"
                                                : "grey.400",
                                            borderColor: showGray
                                                ? "grey.600"
                                                : "grey.300",
                                            fontSize: "0.7rem",
                                            height: 24,
                                            "& .MuiChip-label": { px: 1 },
                                            "&:hover": {
                                                backgroundColor: showGray
                                                    ? "grey.700"
                                                    : "grey.50",
                                            },
                                        }}
                                    />
                                    <Chip
                                        label={`Other LE (${categoryCounts.propertyOwner})`}
                                        size="small"
                                        variant={
                                            showOrange ? "filled" : "outlined"
                                        }
                                        onClick={() =>
                                            setShowOrange(!showOrange)
                                        }
                                        disabled={isCreatingProperties}
                                        sx={{
                                            backgroundColor: showOrange
                                                ? "warning.main"
                                                : "transparent",
                                            color: showOrange
                                                ? "white"
                                                : "warning.light",
                                            borderColor: showOrange
                                                ? "warning.main"
                                                : "warning.light",
                                            fontSize: "0.7rem",
                                            height: 24,
                                            "& .MuiChip-label": { px: 1 },
                                            "&:hover": {
                                                backgroundColor: showOrange
                                                    ? "warning.dark"
                                                    : "warning.50",
                                            },
                                        }}
                                    />
                                    <Chip
                                        label={`New (${categoryCounts.new})`}
                                        size="small"
                                        variant={
                                            showGreen ? "filled" : "outlined"
                                        }
                                        onClick={() => setShowGreen(!showGreen)}
                                        disabled={isCreatingProperties}
                                        sx={{
                                            backgroundColor: showGreen
                                                ? "success.main"
                                                : "transparent",
                                            color: showGreen
                                                ? "white"
                                                : "success.light",
                                            borderColor: showGreen
                                                ? "success.main"
                                                : "success.light",
                                            fontSize: "0.7rem",
                                            height: 24,
                                            "& .MuiChip-label": { px: 1 },
                                            "&:hover": {
                                                backgroundColor: showGreen
                                                    ? "success.dark"
                                                    : "success.50",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Right: Action Buttons */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0.5,
                                        alignItems: "center",
                                    }}
                                >
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleSelectAll}
                                        disabled={
                                            filteredFastApiProperties.filter(
                                                (p) => p.category === "new",
                                            ).length === 0 ||
                                            isCreatingProperties
                                        }
                                        sx={{
                                            minWidth: "auto",
                                            px: 1,
                                            fontSize: "0.7rem",
                                            height: 24,
                                        }}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleClearSelection}
                                        disabled={
                                            selectedPropertyIds.size === 0 ||
                                            isCreatingProperties
                                        }
                                        sx={{
                                            minWidth: "auto",
                                            px: 1,
                                            fontSize: "0.7rem",
                                            height: 24,
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleSubmitSelected}
                                        disabled={
                                            selectedPropertyIds.size === 0 ||
                                            isCreatingProperties
                                        }
                                        startIcon={
                                            isCreatingProperties ? (
                                                <CircularProgress size={16} />
                                            ) : undefined
                                        }
                                        sx={{
                                            background:
                                                selectedPropertyIds.size > 0
                                                    ? "linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)"
                                                    : "grey.300",
                                            color: "white",
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            height: 28,
                                            px: 1.5,
                                            minWidth: 80,
                                            boxShadow:
                                                selectedPropertyIds.size > 0
                                                    ? "0 2px 4px 1px rgba(76, 175, 80, .25)"
                                                    : "none",
                                            "&:hover": {
                                                background:
                                                    selectedPropertyIds.size > 0
                                                        ? "linear-gradient(45deg, #1B5E20 30%, #388E3C 90%)"
                                                        : "grey.300",
                                                boxShadow:
                                                    selectedPropertyIds.size > 0
                                                        ? "0 3px 6px 2px rgba(76, 175, 80, .3)"
                                                        : "none",
                                            },
                                            "&:disabled": {
                                                background: "grey.300",
                                                color: "grey.500",
                                                boxShadow: "none",
                                            },
                                            transition: "all 0.2s ease-in-out",
                                        }}
                                    >
                                        {isCreatingProperties
                                            ? `${
                                                  creationProgress.current + 1
                                              }/${creationProgress.total}`
                                            : `Create (${selectedPropertyIds.size})`}
                                    </Button>
                                </Box>
                            </Box>

                            <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                                <List dense>
                                    {filteredFastApiProperties.map(
                                        (
                                            property: IFastApiPropertyWithStatus,
                                        ) => {
                                            const category =
                                                property.category ?? "new"
                                            const isExisting =
                                                category === "existing"
                                            const isPropertyOwner =
                                                category === "property-owner"
                                            const isNew = category === "new"

                                            // Color scheme based on category
                                            let statusColor = "success.main" // green for new
                                            let backgroundColor = "success.50"
                                            let textColor = "success.dark"

                                            if (isExisting) {
                                                statusColor = "grey.400"
                                                backgroundColor = "grey.50"
                                                textColor = "grey.600"
                                            } else if (isPropertyOwner) {
                                                statusColor = "warning.main" // orange
                                                backgroundColor = "warning.50"
                                                textColor = "warning.dark"
                                            }

                                            return (
                                                <React.Fragment
                                                    key={property.id}
                                                >
                                                    <ListItem
                                                        sx={{
                                                            borderLeft: `3px solid`,
                                                            borderLeftColor:
                                                                statusColor,
                                                            backgroundColor,
                                                            py: 0.25,
                                                            px: 1,
                                                            mb: 0.25,
                                                            borderRadius: 0.5,
                                                            opacity:
                                                                isCreatingProperties
                                                                    ? 0.7
                                                                    : 1,
                                                            minHeight: "auto",
                                                        }}
                                                    >
                                                        {isNew && (
                                                            <Checkbox
                                                                checked={selectedPropertyIds.has(
                                                                    property.id ??
                                                                        "",
                                                                )}
                                                                onChange={() =>
                                                                    property.id !=
                                                                        null &&
                                                                    handleToggleProperty(
                                                                        property.id,
                                                                    )
                                                                }
                                                                disabled={
                                                                    isCreatingProperties
                                                                }
                                                                size="small"
                                                                sx={{
                                                                    color: "success.main",
                                                                    "&.Mui-checked":
                                                                        {
                                                                            color: "success.main",
                                                                        },
                                                                    mr: 0.5,
                                                                    p: 0.25,
                                                                }}
                                                            />
                                                        )}
                                                        <ListItemText
                                                            sx={{ m: 0 }}
                                                            primary={
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "space-between",
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: textColor,
                                                                            fontWeight: 500,
                                                                            fontSize:
                                                                                "0.75rem",
                                                                        }}
                                                                    >
                                                                        {
                                                                            property.name
                                                                        }
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                            gap: 0.5,
                                                                        }}
                                                                    >
                                                                        {isPropertyOwner &&
                                                                            property.legalEntityId !=
                                                                                null && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: "warning.dark",
                                                                                        backgroundColor:
                                                                                            "warning.100",
                                                                                        px: 0.5,
                                                                                        py: 0.125,
                                                                                        borderRadius: 0.25,
                                                                                        fontFamily:
                                                                                            "monospace",
                                                                                        fontSize:
                                                                                            "0.65rem",
                                                                                        fontWeight: 500,
                                                                                    }}
                                                                                >
                                                                                    {getLegalEntityName(
                                                                                        property.legalEntityId,
                                                                                    ) ??
                                                                                        property.legalEntityId}
                                                                                </Typography>
                                                                            )}
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: "text.secondary",
                                                                                backgroundColor:
                                                                                    "grey.200",
                                                                                px: 0.5,
                                                                                py: 0.125,
                                                                                borderRadius: 0.25,
                                                                                fontFamily:
                                                                                    "monospace",
                                                                                fontSize:
                                                                                    "0.7rem",
                                                                            }}
                                                                        >
                                                                            {property.id ??
                                                                                ""}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItem>
                                                </React.Fragment>
                                            )
                                        },
                                    )}
                                    {safeProperties.length === 0 &&
                                        !isLoadingProperties && (
                                            <ListItem>
                                                <ListItemText
                                                    primary={t`fastapi-property-loader.no-fastapi-properties`}
                                                    sx={{
                                                        textAlign: "center",
                                                        color: "text.secondary",
                                                    }}
                                                />
                                            </ListItem>
                                        )}
                                    {filteredFastApiProperties.length === 0 &&
                                        safeProperties.length > 0 &&
                                        !isLoadingProperties && (
                                            <ListItem>
                                                <ListItemText
                                                    primary={t`fastapi-property-loader.no-properties-found`}
                                                    sx={{
                                                        textAlign: "center",
                                                        color: "text.secondary",
                                                    }}
                                                />
                                            </ListItem>
                                        )}
                                    {isLoadingProperties && (
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <CircularProgress
                                                            size={16}
                                                        />
                                                        {t`common.loading`}
                                                    </Box>
                                                }
                                                sx={{
                                                    textAlign: "center",
                                                    color: "text.secondary",
                                                }}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </>
    )
})

export const FastApiPropertyLoaderModal = (
    props: IFastApiPropertyLoaderModalProps,
) => (
    <StoreProvider Store={FastApiPropertyLoaderModalStore}>
        <View {...props} />
    </StoreProvider>
)
